import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ArrowLeft, ChevronLeft, ChevronRight, Eye } from "lucide-react";

// Full paginated history — reused for both appointments and orders. Kind is
// picked up from the route so we only need one component.
export default function BrandCustomerHistory({ kind }) {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const perPage = 25;
  const [customerName, setCustomerName] = useState("");
  const [branches, setBranches] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, cust, br] = await Promise.all([
        staffApi.get(`/customers/${cid}/${kind}`, { params: { page, per_page: perPage } }),
        staffApi.get(`/customers/${cid}`),
        staffApi.get("/branches"),
      ]);
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      const c = cust.data.customer || {};
      setCustomerName(c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Customer");
      setBranches(br.data || []);
    } finally { setLoading(false); }
  }, [cid, kind, page]);
  useEffect(() => { load(); }, [load]);

  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || "—";
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <StaffShell>
      <div className="max-w-6xl mx-auto p-4">
        <button
          type="button"
          onClick={() => navigate(`/staff/brand/customers/${cid}`)}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="hist-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {customerName || "customer"}
        </button>
        <h1 className="text-2xl font-semibold mt-2" data-testid="hist-title">
          {kind === "appointments" ? "All appointments" : "All orders"} · {customerName}
        </h1>
        <p className="text-xs text-muted-foreground">{total} {kind === "appointments" ? "bookings" : "orders"} in total</p>

        <Card className="rounded-lg mt-4">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground" data-testid="hist-empty">Nothing here yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    {kind === "appointments" ? (
                      <TableRow className="h-8">
                        <TableHead className="py-1.5 text-[11px] uppercase">Booking #</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Status</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Service</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Appointment</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Location</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase text-right">Bill</TableHead>
                      </TableRow>
                    ) : (
                      <TableRow className="h-8">
                        <TableHead className="py-1.5 text-[11px] uppercase">Order #</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Status</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Total</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Location</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase">Created</TableHead>
                        <TableHead className="py-1.5 text-[11px] uppercase text-right">Open</TableHead>
                      </TableRow>
                    )}
                  </TableHeader>
                  <TableBody>
                    {kind === "appointments"
                      ? rows.map((a) => (
                          <TableRow key={a.id} className="h-9" data-testid={`hist-appt-${a.id}`}>
                            <TableCell className="py-1.5 text-xs text-muted-foreground font-mono">{a.id.slice(0, 8).toUpperCase()}</TableCell>
                            <TableCell className="py-1.5 text-xs capitalize">{(a.status || "booked").replace("_", " ")}</TableCell>
                            <TableCell className="py-1.5 text-xs">{a.service_name}</TableCell>
                            <TableCell className="py-1.5 text-xs whitespace-nowrap">{formatBali(a.start_iso)}</TableCell>
                            <TableCell className="py-1.5 text-xs text-muted-foreground">{branchName(a.branch_id)}</TableCell>
                            <TableCell className="py-1.5 text-right">
                              {/* Eye is always available — no order yet ⇒ open via ?appt=<id> which seeds a draft. */}
                              <button
                                type="button"
                                onClick={() => navigate(
                                  a.order_id
                                    ? `/staff/branch/${a.branch_id}/orders?order=${a.order_id}`
                                    : `/staff/branch/${a.branch_id}/orders?appt=${a.id}`
                                )}
                                className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-primary/10 text-primary"
                                title={a.order_id ? "Open order" : "Open checkout for this booking"}
                                data-testid={`hist-appt-bill-${a.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                            </TableCell>
                          </TableRow>
                        ))
                      : rows.map((o) => (
                          <TableRow key={o.id} className="h-9" data-testid={`hist-order-${o.id}`}>
                            <TableCell className="py-1.5 text-xs text-muted-foreground font-mono">{o.order_number || o.id.slice(0, 8).toUpperCase()}</TableCell>
                            <TableCell className="py-1.5 text-xs capitalize">{o.status}</TableCell>
                            <TableCell className="py-1.5 text-xs whitespace-nowrap">Rp {Number(o.total || 0).toLocaleString("id-ID")}</TableCell>
                            <TableCell className="py-1.5 text-xs text-muted-foreground">{branchName(o.branch_id)}</TableCell>
                            <TableCell className="py-1.5 text-xs text-muted-foreground">{formatBali(o.created_at)}</TableCell>
                            <TableCell className="py-1.5 text-right">
                              <button type="button" onClick={() => navigate(`/staff/branch/${o.branch_id}/orders?order=${o.id}`)} className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-primary/10 text-primary" title="Open order" data-testid={`hist-order-open-${o.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-3 text-xs" data-testid="hist-pager">
            <Button size="sm" variant="outline" className="h-7" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="hist-prev-btn"><ChevronLeft className="w-3 h-3" /> Prev</Button>
            <span className="text-muted-foreground">Page {page} of {totalPages}</span>
            <Button size="sm" variant="outline" className="h-7" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="hist-next-btn">Next <ChevronRight className="w-3 h-3" /></Button>
          </div>
        )}
      </div>
    </StaffShell>
  );
}

function formatBali(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar",
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}
